export default (() => {

    class DraggableImage {
        // Canvas items
        #canvas;
        #context;
        #image;
        // Alteration settings
        #rotation = 0;
        #size = 1;
        // Move settings
        #imageCenterX = null;
        #imageCenterY = null;
        #imageMouseOffsetX = null;
        #imageMouseOffsetY = null;
        #touchTarget = null;
        // Key drag settings
        #keyDragTickMilliseconds = 10;
        #keyDragIncrement = 1;
        #arrowKeyLeft = 37;
        #arrowKeyUp = 38;
        #arrowKeyRight = 39;
        #arrowKeyDown = 40;
        #keyTab = 9;
        #keyDragType = null;
        #keyDragTimeout = null;
        // Flags
        #isDragging = false;
        #isChangingByKey = false;

        constructor (canvasElement, imageURL) {
            this.#canvas = canvasElement;

            this.#initialiseCanvas(imageURL);
            this.#initialiseDrag();
        }

        rotate () {
            this.#rotation = this.#rotation !== 270 ? this.#rotation + 90 : 0;

            this.#context.translate(this.#canvas.width / 2, this.#canvas.height / 2);
            this.#context.rotate(90 * Math.PI / 180);
            this.#context.translate(-this.#canvas.width / 2, -this.#canvas.height / 2);
            this.#drawImage();
        }

        reset () {
            this.#rotation = 0;
            this.#size = 1;
            this.#context.setTransform(1, 0, 0, 1, 0, 0);
            this.#setImageCenter();
            this.#drawImage();
        }

        resize (fraction) {
            this.#size = fraction;
            this.#drawImage();
        }

        get toDataURL () {
            return this.#canvas.toDataURL();
        }

        get toBase64String () {
            return this.#canvas.toDataURL().split('base64,')[1];
        }

        get #imageDrawWidth () {
            const {
                ratio, isLandscape
            } = this.#imageToHeightRatioDetails;
            const constrainedWidth = isLandscape ? this.#canvas.width : this.#canvas.height / ratio;

            return constrainedWidth * this.#size;
        }

        get #imageDrawHeight () {
            const {
                ratio, isLandscape
            } = this.#imageToHeightRatioDetails;
            const constrainedHeight =  !isLandscape ? this.#canvas.height : this.#canvas.width / ratio;
            
            return constrainedHeight * this.#size;
        }

        get #imageOffsetX () {
            return this.#imageCenterX - (this.#imageDrawWidth / 2);
        }

        get #imageOffsetY () {
            return this.#imageCenterY - (this.#imageDrawHeight / 2);
        }

        get #imageToHeightRatioDetails () {
            const isLandscape = Math.max(this.#image.width, this.#image.height) === this.#image.width;
            const ratio = isLandscape ? this.#image.width / this.#image.height : this.#image.height / this.#image.width;

            return {
                ratio,
                isLandscape
            };
        }

        get #imageRotateOffSetMap () {
            const imageRotateOffsetMap = {
                0: [this.#imageOffsetX, this.#imageOffsetY],
                90: [((this.#canvas.width - this.#canvas.height) / 2) + this.#imageCenterY - (this.#imageDrawWidth / 2), (this.#canvas.width - ((this.#canvas.width - this.#canvas.height) / 2)) - this.#imageCenterX - (this.#imageDrawHeight / 2)],
                180: [this.#canvas.width - this.#imageDrawWidth - this.#imageOffsetX, this.#canvas.height - this.#imageDrawHeight - this.#imageOffsetY],
                270: [(this.#canvas.width - ((this.#canvas.width - this.#canvas.height) / 2)) - this.#imageCenterY - (this.#imageDrawWidth / 2), - ((this.#canvas.width - this.#canvas.height) / 2) + this.#imageCenterX - (this.#imageDrawHeight / 2) ],
            };

            return imageRotateOffsetMap[this.#rotation];
        }

        #initialiseCanvas (imageURL) {
            this.#canvas.width = this.#canvas.offsetWidth;
            this.#canvas.height = this.#canvas.offsetHeight;

            this.#image = new Image();
            this.#image.crossOrigin = '*';
            this.#context = this.#canvas.getContext('2d');
            this.#image.src = imageURL;

            this.#setImageCenter();

            this.#image.onload = () => {
                this.#drawImage();
            };
        }

        #setImageCenter () {
            this.#imageCenterX = this.#canvas.width / 2;
            this.#imageCenterY = this.#canvas.height / 2;
        }

        #drawImage () {
            this.#context.save();
            this.#context.setTransform(1, 0, 0, 1, 0, 0);
            this.#context.clearRect(0, 0, this.#canvas.width, this.#canvas.height);
            this.#context.restore();
            this.#context.drawImage(this.#image, ...this.#imageRotateOffSetMap, this.#imageDrawWidth, this.#imageDrawHeight);
            this.#fireCanvasChangeEvent();
        }

        #fireCanvasChangeEvent () {
            this.#canvas.dispatchEvent(new Event('canvaschange'));
        }

        #toggleDraggingCursor () {
            this.#canvas.style.cursor = this.#isDragging ? 'grabbing' : 'grab';
        }

        #initialiseDrag () {
            this.#canvas.addEventListener('mousedown', this.#activateMoveWithMouse.bind(this));
            this.#canvas.addEventListener('mouseup', this.#deactivateMoveWithMouse.bind(this));
            this.#canvas.addEventListener('mouseleave', this.#deactivateMoveWithMouse.bind(this));
            this.#canvas.addEventListener('mousemove', this.#move.bind(this));
            this.#canvas.addEventListener('mouseenter', this.#toggleDraggingCursor.bind(this));
            
            // Prevent text from being selected
            // while dragging from the canvas
            this.#canvas.addEventListener('selectstart', (event) => {
                event.preventDefault();
                
                return false;
            });

            this.#canvas.addEventListener('touchstart', this.#activateMoveWithTouch.bind(this));
            this.#canvas.addEventListener('touchend', this.#deactivateMove.bind(this));
            this.#canvas.addEventListener('touchmove', this.#moveWithTouch.bind(this));

            this.#canvas.addEventListener('keydown', this.#activateMoveWithKey.bind(this));
            this.#canvas.addEventListener('keyup', this.#deactivateMoveWithKey.bind(this));
        }

        #activateMoveWithMouse (event) {
            this.#activateMove(event);
            this.#toggleDraggingCursor();
        }

        #activateMove (event) {
            this.#isDragging = true;
    
            this.#imageMouseOffsetX = event.offsetX - this.#imageCenterX;
            this.#imageMouseOffsetY = event.offsetY - this.#imageCenterY;
        }
          
        #deactivateMoveWithMouse () {
            this.#deactivateMove();
            this.#toggleDraggingCursor();
        }
          
        #deactivateMove () {
            this.#isDragging = false;
            this.#touchTarget = null;
        }

        #move (event) {
            const isInvalidTouchEvent = event.type === 'touchmove' && this.#canvas !== this.#touchTarget;

            if (this.#isChangingByKey || !this.#isDragging || isInvalidTouchEvent) {
                return;
            }

            this.#imageCenterX = event.offsetX - this.#imageMouseOffsetX;
            this.#imageCenterY = event.offsetY - this.#imageMouseOffsetY;

            this.#drawImage();
        }

        #activateMoveWithTouch (event) {
            this.#addTouchOffsets(event);
            this.#activateMove(event);
        }

        #moveWithTouch (event) {
            event.preventDefault();
            this.#addTouchOffsets(event);
            this.#move(event);
        }

        #addTouchOffsets (event) {
            const touch = event.touches[0] || event.changedTouches[0];
            
            this.#touchTarget = document.elementFromPoint(touch.clientX, touch.clientY);

            event.offsetX = touch.clientX - this.#touchTarget.getBoundingClientRect().x;
            event.offsetY = touch.clientY - this.#touchTarget.getBoundingClientRect().y;
        }

        #activateMoveWithKey (event) {
            const isTabKey = event.keyCode === this.#keyTab;

            if (isTabKey) {
                return;
            }

            event.preventDefault();

            if (this.#isChangingByKey) {
                return;
            }

            const isArrowKey = event.keyCode >= this.#arrowKeyLeft && event.keyCode <= this.#arrowKeyDown;

            if (!isArrowKey) {
                return;
            }

            this.#isChangingByKey = true;

            this.#activateMove(event);
            this.#keyDragType = event.keyCode;
            this.#keyDragTimeout = setInterval(this.#dragWithKey.bind(this), this.#keyDragTickMilliseconds);
        }

        #deactivateMoveWithKey () {
            this.#isChangingByKey = false;
            this.#deactivateMove(); 
            clearInterval(this.#keyDragTimeout);
            this.#keyDragTimeout = null;
            this.#keyDragType = null;
        }

        #dragWithKey () {
            if (!this.#isChangingByKey || !this.#isDragging) {
                return;
            }

            const isOffsetX = this.#keyDragType === this.#arrowKeyLeft || this.#keyDragType === this.#arrowKeyRight;
            const offsetKeyCodeMap = {
                [this.#arrowKeyLeft]: this.#imageCenterX - this.#keyDragIncrement,
                [this.#arrowKeyUp]: this.#imageCenterY - this.#keyDragIncrement,
                [this.#arrowKeyRight]: this.#imageCenterX + this.#keyDragIncrement,
                [this.#arrowKeyDown]: this.#imageCenterY + this.#keyDragIncrement,
            };

            this.#imageCenterX = isOffsetX ? offsetKeyCodeMap[this.#keyDragType] : this.#imageCenterX;
            this.#imageCenterY = !isOffsetX ? offsetKeyCodeMap[this.#keyDragType] : this.#imageCenterY;
    
            this.#drawImage();
        }
    }

    return {DraggableImage};
})();